import { Alert, Button, Card, Checkbox, FieldBlock, Input, Select, TagSelect, Textarea } from '@orbind/ui'
import { useMemo, useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router'
import { OrbindApiError } from '@orbind/api-client'
import { api } from '../lib/api.ts'
import { OrbindLogo } from '../shell/OrbindLogo.tsx'
import './login.css'

// codigos estaveis (casam com apps/api onboarding/routes.ts) + rotulos pt-BR
const TEAM_SIZES = [
  { value: '1', label: 'Somente eu' },
  { value: '2_TO_5', label: '2 a 5' },
  { value: '6_TO_10', label: '6 a 10' },
  { value: '11_TO_25', label: '11 a 25' },
  { value: '26_TO_50', label: '26 a 50' },
  { value: '51_TO_100', label: '51 a 100' },
  { value: '101_TO_250', label: '101 a 250' },
  { value: '251_PLUS', label: 'Mais de 250' },
] as const
const PRIMARY_AREAS = [
  { value: 'OPERATIONS', label: 'Operação' },
  { value: 'PRODUCT', label: 'Produto' },
  { value: 'DEVELOPMENT', label: 'Desenvolvimento' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'SALES', label: 'Vendas' },
  { value: 'CUSTOMER_SUCCESS', label: 'Sucesso do Cliente' },
  { value: 'ADMINISTRATION', label: 'Administrativo' },
  { value: 'OTHER', label: 'Outra' },
] as const
/*
 * `DEMANDS` existe no enum do servidor e NÃO é oferecido aqui: Demanda foi
 * removida do produto em 2026-08-26, e perguntar o interesse de alguém numa
 * funcionalidade que não existe é prometer o que não há. O valor segue aceito
 * pela API por causa dos pedidos antigos, que o guardam.
 */
const INTERESTS = [
  { value: 'WORK_ITEMS', label: 'Itens de Trabalho' },
  { value: 'SOFTWARE_DEVELOPMENT', label: 'Desenvolvimento de software' },
  { value: 'OPERATIONS', label: 'Operação' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'TEAM_COMMUNICATION', label: 'Comunicação entre equipes' },
  { value: 'AUTOMATIONS', label: 'Automações' },
  { value: 'ALL_IN_ONE', label: 'Tudo em um único ambiente' },
] as const

const DESAFIO_MAX = 1000

type Tela = 'intro' | 'form' | 'success' | 'email-exists' | 'duplicate'

/**
 * Solicitacao PUBLICA de convite (2026-08-26): substitui o cadastro publico
 * direto. Captura o interessado e registra o pedido para analise posterior.
 * Reaproveita o layout e o CSS da tela de login (login.css).
 */
export function RequestInvitePage() {
  const navigate = useNavigate()
  const [tela, setTela] = useState<Tela>('intro')
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [teamSize, setTeamSize] = useState<string | null>(null)
  const [primaryArea, setPrimaryArea] = useState<string | null>(null)
  const [interests, setInterests] = useState<string[]>([])
  const [mainChallenge, setMainChallenge] = useState('')
  const [consent, setConsent] = useState(false)

  const emailValido = /.+@.+\..+/.test(email.trim())
  /*
   * ANTES do `return` abaixo, e não depois (2026-09-21).
   *
   * Estava embaixo dele: se `isAuthenticated` virasse com a tela montada — a
   * pessoa entra noutra aba e volta — o componente passaria a rodar com um
   * hook a menos e o React derrubaria a tela (erro #310). É o mesmo defeito
   * que quebrou o Kanban em produção neste dia; aqui só não tinha estourado.
   */
  const podeEnviar = useMemo(
    () =>
      fullName.trim().length >= 2 &&
      emailValido &&
      companyName.trim().length >= 1 &&
      teamSize !== null &&
      primaryArea !== null &&
      consent,
    [fullName, emailValido, companyName, teamSize, primaryArea, consent],
  )

  if (api.isAuthenticated) return <Navigate to="/home" replace />

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (!podeEnviar || enviando) return
    setEnviando(true)
    setErro(null)
    try {
      const res = await api.onboarding['access-request'].$post({
        json: {
          fullName: fullName.trim(),
          email: email.trim(),
          companyName: companyName.trim(),
          teamSize: teamSize as never,
          primaryArea: primaryArea as never,
          interests: interests as never,
          mainChallenge: mainChallenge.trim() || undefined,
          consent,
        },
      })
      if (res.status === 201) setTela('success')
    } catch (e) {
      /*
       * O cliente LANÇA em qualquer não-2xx (api-client/src/index.ts). O código
       * aqui tratava a resposta como se ela voltasse, então os dois casos de
       * 409 eram código morto e tudo caía no "falha de conexão" —
       * SPEC-20261001-0948. Agora o motivo vem do erro.
       */
      const codigo = e instanceof OrbindApiError ? e.code : null
      if (codigo === 'EMAIL_HAS_ACCOUNT') setTela('email-exists')
      else if (codigo === 'REQUEST_EXISTS') setTela('duplicate')
      else if (e instanceof OrbindApiError) {
        // erro do servidor: a mensagem dele é mais útil que a nossa genérica
        setErro(e.message || 'Não foi possível enviar sua solicitação. Tente novamente.')
      } else {
        // aqui sim é rede: o fetch nem chegou a receber resposta
        setErro('Falha de comunicação. Verifique sua conexão e tente novamente.')
      }
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="ob-root login">
      <section className="login__brand" aria-hidden>
        <div className="login__logo">
          <OrbindLogo size={44} color="#FFFFFF" halo="var(--ob-primary)" />
        </div>
        {/*
          O painel fala do CONVITE, não do produto (SPEC-20261001-2247). Quem
          está nesta tela já decidiu que quer; o que falta é motivo para pedir
          agora em vez de depois.
        */}
        <h1 className="login__brand-title">Os primeiros times já estão dentro.</h1>
        <p className="login__brand-text">
          O Orbind abre por convite, em lotes pequenos. Quem entra agora usa o produto enquanto ele ainda está tomando
          forma — e ajuda a decidir o que vem depois.
        </p>

        <ul className="login__features">
          {/* as quatro perguntas de quem está com o dedo no botão: quando eu
              entro, preciso pedir de novo, adianta eu falar, e quem me atende */}
          <li>
            <span className="login__feat-ico">
              <i className="fas fa-rocket" aria-hidden />
            </span>
            <span className="login__feat-txt">
              <strong>Entre antes da fila</strong>
              O acesso é liberado aos poucos. Quem passa agora já está trabalhando enquanto a maioria ainda espera.
            </span>
          </li>
          <li>
            <span className="login__feat-ico">
              <i className="fas fa-bookmark" aria-hidden />
            </span>
            <span className="login__feat-txt">
              <strong>Seu lugar fica guardado</strong>
              Um pedido basta. Ele fica registrado e você é avisado por e-mail assim que for a sua vez.
            </span>
          </li>
          <li>
            <span className="login__feat-ico">
              <i className="fas fa-comments" aria-hidden />
            </span>
            <span className="login__feat-txt">
              <strong>Sua opinião pesa mais agora</strong>
              Com poucos times dentro, o que você pedir é ouvido antes da fila de prioridades encher.
            </span>
          </li>
          <li>
            <span className="login__feat-ico">
              <i className="fas fa-headset" aria-hidden />
            </span>
            <span className="login__feat-txt">
              <strong>Fale com quem constrói</strong>
              Enquanto o grupo é pequeno, quem responde é quem faz o produto.
            </span>
          </li>
        </ul>

        <div className="login__chips">
          <span>Portfólio</span>
          <span>Itens de Trabalho</span>
          <span>Kanban</span>
          <span>Automações</span>
          <span>Métricas</span>
        </div>
      </section>

      <section className="login__panel">
        <Card className="login__card">
          {tela === 'intro' ? (
            <div className="ob-stack" style={{ textAlign: 'center' }}>
              <h2 className="login__title login__title--hero">Seu trabalho merece um lugar melhor para acontecer.</h2>
              <p className="login__subtitle">
                Estamos criando uma nova forma de conectar equipes, Itens de Trabalho, produtos e automações em um
                único ambiente.
              </p>
              <p className="login__subtitle" style={{ marginTop: -4 }}>
                O acesso está sendo liberado gradualmente para os primeiros times.
              </p>
              <Button variant="primary" onClick={() => setTela('form')}>
                Solicitar meu convite
              </Button>
              <p style={{ fontSize: 13, margin: '4px 0 0', textAlign: 'center' }}>
                Já tem conta?{' '}
                <a className="login__link" href="/login">
                  Entrar
                </a>
              </p>
            </div>
          ) : tela === 'form' ? (
            <>
              <h2 className="login__title">Solicitar convite</h2>
              <p className="login__subtitle">
                Conte um pouco sobre você e sua equipe. Analisamos cada solicitação e enviaremos um convite assim que
                seu acesso estiver disponível.
              </p>

              <form className="ob-stack" onSubmit={onSubmit} noValidate>
                {erro ? (
                  <Alert tone="danger" title="Não foi possível enviar">
                    {erro}
                  </Alert>
                ) : null}

                <FieldBlock label="Nome completo" required>
                  {({ id, invalid }) => (
                    <Input
                      id={id}
                      autoComplete="name"
                      required
                      invalid={invalid}
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Seu nome"
                    />
                  )}
                </FieldBlock>

                <FieldBlock label="E-mail profissional" required>
                  {({ id, invalid }) => (
                    <Input
                      id={id}
                      type="email"
                      autoComplete="email"
                      required
                      invalid={invalid}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="voce@empresa.com.br"
                    />
                  )}
                </FieldBlock>

                <FieldBlock label="Empresa" required>
                  {({ id, invalid }) => (
                    <Input
                      id={id}
                      autoComplete="organization"
                      required
                      invalid={invalid}
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Nome da empresa"
                    />
                  )}
                </FieldBlock>

                <FieldBlock label="Tamanho da equipe" required>
                  {({ id }) => (
                    <Select
                      id={id}
                      value={teamSize}
                      options={TEAM_SIZES.map((o) => ({ value: o.value, label: o.label }))}
                      placeholder="Selecione"
                      onChange={setTeamSize}
                    />
                  )}
                </FieldBlock>

                <FieldBlock label="Qual é a principal área da sua equipe?" required>
                  {({ id }) => (
                    <Select
                      id={id}
                      value={primaryArea}
                      options={PRIMARY_AREAS.map((o) => ({ value: o.value, label: o.label }))}
                      placeholder="Selecione"
                      onChange={setPrimaryArea}
                    />
                  )}
                </FieldBlock>

                <FieldBlock label="O que você gostaria de organizar melhor?" hint="Você pode escolher mais de um.">
                  {({ id }) => (
                    <TagSelect
                      id={id}
                      value={interests}
                      options={INTERESTS.map((o) => ({ value: o.value, label: o.label }))}
                      placeholder="Selecione um ou mais"
                      onChange={setInterests}
                    />
                  )}
                </FieldBlock>

                <FieldBlock
                  label="Qual é o principal desafio que você gostaria de resolver?"
                  hint={`${mainChallenge.length}/${DESAFIO_MAX}`}
                >
                  {({ id, invalid }) => (
                    <Textarea
                      id={id}
                      rows={4}
                      maxLength={DESAFIO_MAX}
                      invalid={invalid}
                      value={mainChallenge}
                      onChange={(e) => setMainChallenge(e.target.value)}
                      placeholder="Conte brevemente como sua equipe trabalha hoje e o que você gostaria de melhorar."
                    />
                  )}
                </FieldBlock>

                <Checkbox
                  label="Concordo em receber comunicações relacionadas ao meu pedido de acesso."
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />

                <Button type="submit" variant="primary" loading={enviando} disabled={!podeEnviar}>
                  Solicitar meu convite
                </Button>

                <p style={{ fontSize: 13, margin: '4px 0 0', textAlign: 'center' }}>
                  Já tem conta?{' '}
                  <a className="login__link" href="/login">
                    Entrar
                  </a>
                </p>
              </form>
            </>
          ) : tela === 'success' ? (
            <div className="ob-stack" style={{ textAlign: 'center' }}>
              <div className="login__success-ico" aria-hidden>
                <i className="fas fa-circle-check" />
              </div>
              <h2 className="login__title">Pedido recebido.</h2>
              <p className="login__subtitle">
                Seu interesse já foi registrado. Estamos liberando novos acessos gradualmente para garantir uma boa
                experiência desde o primeiro dia. Quando chegar a sua vez, enviaremos seu convite por e-mail.
              </p>
              <Button variant="secondary" onClick={() => navigate('/login')}>
                Voltar ao início
              </Button>
            </div>
          ) : tela === 'email-exists' ? (
            <div className="ob-stack" style={{ textAlign: 'center' }}>
              <div className="login__success-ico login__success-ico--info" aria-hidden>
                <i className="fas fa-user-check" />
              </div>
              <h2 className="login__title">Este e-mail já possui uma conta.</h2>
              <p className="login__subtitle">Use suas credenciais para entrar.</p>
              <Button variant="primary" onClick={() => navigate('/login')}>
                Entrar na minha conta
              </Button>
            </div>
          ) : (
            <div className="ob-stack" style={{ textAlign: 'center' }}>
              <div className="login__success-ico login__success-ico--info" aria-hidden>
                <i className="fas fa-clock" />
              </div>
              <h2 className="login__title">Já recebemos seu pedido de acesso.</h2>
              <p className="login__subtitle">
                Estamos liberando novos acessos gradualmente. Quando seu convite estiver disponível, enviaremos uma
                mensagem para este e-mail.
              </p>
              <Button variant="secondary" onClick={() => navigate('/login')}>
                Voltar ao início
              </Button>
            </div>
          )}
        </Card>
      </section>
    </div>
  )
}
